import dayjs from "dayjs";
import "dayjs/locale/en";
import "dayjs/locale/pl";
import { localeMessages, LOCALES } from "i18n/messages";

import React, { useEffect, useState } from "react";
import { IntlProvider } from "react-intl";

type LanguageContextType = {
  locale: string;
  selectLanguage: (newLocale: string) => void;
};
export const LanguageContext = React.createContext<LanguageContextType | null>(null);
const SupportedLanguages = Object.values(LOCALES);
const DEFAULT_LOCALE = LOCALES.POLISH;

function determineLocale() {
  const pathSegments = location.pathname.split("/");
  const localeInPath = pathSegments[1];
  if (SupportedLanguages.includes(localeInPath)) {
    return localeInPath;
  }

  return DEFAULT_LOCALE;
}

function determineNativeLocale() {
  const browserLanguage = navigator.language.split("-")[0];
  return SupportedLanguages.includes(browserLanguage) ? browserLanguage : DEFAULT_LOCALE;
}

const LangProvider = ({ children }) => {
  const [locale, setLocale] = useState(determineLocale);
  const [messages, setMessages] = useState(localeMessages[locale]);

  // Adding the function to the dependencies ensures that it will only be excecuted once
  useEffect(() => {
    if (locale === LOCALES.ENGLISH) {
      const nativeLocale = determineNativeLocale();
      selectLanguage(nativeLocale);
    }
  }, [determineNativeLocale]);

  useEffect(() => {
    dayjs.locale(locale);
  }, [locale]);

  const selectLanguage = (newLocale: string) => {
    setLocale(newLocale);
    setMessages(localeMessages[newLocale]);
  };

  return (
    <LanguageContext.Provider value={{ locale, selectLanguage }}>
      <IntlProvider messages={messages} locale={locale} defaultLocale={DEFAULT_LOCALE}>
        {children}
      </IntlProvider>
    </LanguageContext.Provider>
  );
};

export default LangProvider;
